import React from "react";
import SkeletonBlock from "./SkeletonBlock";
import { PrimaryButton } from "@/components/ds";

interface Props {
  /** When true, renders just the SVG (for table cells, buttons, inline labels).
   *  Default: renders a content-shaped skeleton block with reserved vertical space
   *  so first paint is stable and nothing jumps from top-left to center
   *  as the surrounding layout (sidebar/topbar) finishes hydrating. */
  inline?: boolean;
  /** Pixel size of the SVG itself (inline mode only). Default 36. */
  size?: number;
  /** Minimum block height when not inline. Default 240. */
  minHeight?: number;
  /** After this many ms still loading, show "Taking longer than usual…" + Retry.
   *  A swallowed fetch error can leave the skeleton up forever — this is the escape hatch.
   *  Default 12000. Set 0 to disable. */
  stuckAfterMs?: number;
}

function Svg({
  size = 36,
  className = ""
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 36 36"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ animation: "v-spin 0.9s linear infinite" }}
    >
      <style>{`@keyframes v-spin { to { transform: rotate(360deg); } }`}</style>
      <circle
        cx="18"
        cy="18"
        r="15"
        fill="none"
        stroke="#e5e7eb"
        strokeWidth="3"
      />
      <circle
        cx="18"
        cy="18"
        r="15"
        fill="none"
        stroke="#4C75AB"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="70 30"
      />
    </svg>
  );
}

/** One placeholder "row" — a short label-shaped bar over a wider control-shaped
 *  bar, echoing the label+control rhythm of the settings/list/panel content that
 *  is actually about to load (not a single flat gray rectangle). */
function GenericSkeletonRow({ wide }: { wide: string }) {
  return (
    <div className="flex flex-col gap-2 w-full" style={{ maxWidth: wide }}>
      <SkeletonBlock height={11} width="35%" />
      <SkeletonBlock height={34} width="100%" className="rounded-lg" />
    </div>
  );
}

/** Fills roughly `minHeight` px with a handful of row-shaped skeleton bars so the
 *  loading state reads as "content is loading here" rather than a plain blob. */
function GenericSkeleton({ minHeight }: { minHeight: number }) {
  const rowHeight = 58; // ~label + control + gap
  const rows = Math.max(2, Math.min(6, Math.round(minHeight / rowHeight)));
  const widths = ["92%", "76%", "88%", "64%", "82%", "70%"];
  return (
    <div
      className="v-fade-in flex flex-col gap-5 w-full max-w-xl mx-auto py-2"
      style={{ minHeight }}
      aria-busy="true"
      aria-label="Loading content"
    >
      {Array.from({ length: rows }).map((_, i) => (
        <GenericSkeletonRow key={i} wide={widths[i % widths.length]} />
      ))}
    </div>
  );
}

/** Delay before the spinner is allowed to render at all, so a fast load
 *  (data already cached / resolves in a couple hundred ms) never flashes a
 *  spinner the user barely perceives — it just looks like content popped in. */
const SHOW_DELAY_MS = 200;

export default function LoadingSpinner({
  inline = false,
  size,
  minHeight = 240,
  stuckAfterMs = 12000
}: Props) {
  // Inline spinners (button/save feedback) must render immediately — a delay there reads as an
  // unresponsive click, not a smoothed load. The delay only applies to full-block spinners, where
  // it prevents a flash on loads that resolve in well under 200ms.
  const [shouldShow, setShouldShow] = React.useState(inline);
  const [stuck, setStuck] = React.useState(false);

  React.useEffect(() => {
    if (inline) return;
    const t = window.setTimeout(() => setShouldShow(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [inline]);

  React.useEffect(() => {
    if (inline || !stuckAfterMs) return;
    const t = window.setTimeout(() => setStuck(true), stuckAfterMs);
    return () => window.clearTimeout(t);
  }, [inline, stuckAfterMs]);

  if (!shouldShow) return null;

  if (inline) return <Svg size={size} />;
  return (
    <div className="flex flex-col items-center justify-center w-full gap-3">
      <GenericSkeleton minHeight={minHeight} />
      {stuck && (
        <div style={{ textAlign: "center", maxWidth: 320 }}>
          <div style={{ color: "#374151", fontSize: 13, marginBottom: 8 }}>
            Taking longer than usual…
          </div>
          <PrimaryButton onClick={() => window.location.reload()} className="!text-xs !py-1.5">
            Retry
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}
